import { MessageEntity, MessageContentEntity } from "@/indexedDB/types";

/**
 * Purpose: Deep comparison utility for message entities and message props
 *
 * How it works:
 * 1. Performs deep equality check on MessageEntity objects
 * 2. Handles nested objects, arrays, and primitive values
 * 3. Provides specialized comparison for MessageItem props
 *
 * Key features:
 * - Type-safe comparisons with TypeScript
 * - Handles undefined/null values
 * - Performance optimized with early returns
 */

/**
 * Deep comparison of two values (objects, arrays, or primitives)
 * Generic helper function for deep equality checks
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDeepEqual(a: any, b: any): boolean {
  // Handle primitive types and referential equality
  if (a === b) {
    return true;
  }

  // Handle null/undefined cases
  if (a == null || b == null) {
    return a === b;
  }

  // Check if both are objects (including arrays)
  if (typeof a !== "object" || typeof b !== "object") {
    return false;
  }

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i++) {
      if (!isDeepEqual(a[i], b[i])) {
        return false;
      }
    }

    return true;
  }

  // Handle regular objects
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (!keysB.includes(key)) {
      return false;
    }
    if (!isDeepEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
}

/**
 * Deep comparison specifically for MessageContentEntity objects
 * Handles the nested structure of message content
 */
export function areMessageContentsEqualDeep(
  prevContent: MessageContentEntity | undefined,
  nextContent: MessageContentEntity | undefined,
): boolean {
  // Handle undefined/null cases
  if (!prevContent && !nextContent) {
    return true;
  }
  if (!prevContent || !nextContent) {
    return false;
  }

  // Compare all fields of MessageContentEntity
  return (
    prevContent.text === nextContent.text &&
    isDeepEqual(prevContent.media, nextContent.media) &&
    isDeepEqual(prevContent.voice, nextContent.voice) &&
    isDeepEqual(prevContent.file, nextContent.file) &&
    prevContent.link === nextContent.link
  );
}

/**
 * Deep comparison specifically for MessageEntity objects
 * Optimized for the specific structure of MessageEntity
 */
export function areMessagesEqualDeep(
  prevMessage: MessageEntity | undefined,
  nextMessage: MessageEntity | undefined,
): boolean {
  // Handle undefined/null cases
  if (!prevMessage && !nextMessage) {
    return true;
  }
  if (!prevMessage || !nextMessage) {
    return false;
  }

  // Compare all fields of MessageEntity
  return (
    prevMessage.id === nextMessage.id &&
    prevMessage.chatId === nextMessage.chatId &&
    areMessageContentsEqualDeep(prevMessage.content, nextMessage.content) &&
    prevMessage.sender === nextMessage.sender &&
    prevMessage.avatarId === nextMessage.avatarId &&
    prevMessage.timestamp === nextMessage.timestamp &&
    prevMessage.category === nextMessage.category &&
    prevMessage.sendingStatus === nextMessage.sendingStatus &&
    prevMessage.hasAttachment === nextMessage.hasAttachment &&
    prevMessage.isEdited === nextMessage.isEdited &&
    prevMessage.isOutgoing === nextMessage.isOutgoing &&
    prevMessage.isReply === nextMessage.isReply &&
    prevMessage.commentCount == nextMessage.commentCount &&
    prevMessage.isPinned === nextMessage.isPinned &&
    isDeepEqual(prevMessage.parentMessage, nextMessage.parentMessage) &&
    isDeepEqual(prevMessage.reactions, nextMessage.reactions) &&
    isDeepEqual(prevMessage.userReaction, nextMessage.userReaction)
  );
}

/**
 * Type for MessageItem props to ensure type safety
 */
interface MessageItemProps {
  message: MessageEntity;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}

/**
 * Compare MessageItem props for React.memo
 * Can be used as the equality function for memo()
 */
export function areMessageItemPropsEqual(
  prevProps: MessageItemProps,
  nextProps: MessageItemProps,
): boolean {
  return (
    areMessagesEqualDeep(prevProps.message, nextProps.message) &&
    prevProps.isFirstInGroup === nextProps.isFirstInGroup &&
    prevProps.isLastInGroup === nextProps.isLastInGroup
  );
}
